import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { differenceInYears, format, isAfter, isValid, parse } from 'date-fns';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { EverysetLogo } from '@assets/img';
import SaaSLogo from '@assets/img/SaaSLogo';
import { Button } from '@components/Button';
import { ThankyouModal } from '@components/ThankyouModal/ThankyouModal';
import { useClientEnvironment } from '@hooks/clientEnvironment';
import { useModalVisibility } from '@hooks/shared/useModalVisibility';
import { RootState } from '@lib/redux.lib/store';

import { useSignupPageContext } from '../SignupPageContext';
import { StyledGapContainer } from '../styles';

import { BirthDatePicker } from './BirthDatePicker/BirthDatePicker';
import { CountryFlags } from './CountryFlags/CountryFlags';

export const CountrySelection = (): JSX.Element => {
  const {
    isUkSelected,
    setCurrentPage,
    isUsaSelected,
    birthDateString,
    setBirthDateString,
  } = useSignupPageContext();

  const router = useRouter();
  const userNeedsNewPassword = !!router.query?.newPasswordRequired;
  const tempUserInfo = useSelector(
    (state: RootState) => state.app.user.tempUser
  );

  useEffect(() => {
    const validateUserWithTempPwdInfo = () => {
      if (userNeedsNewPassword) {
        if (!tempUserInfo) {
          router.push('/auth/login'); 
          return;
        }

        const email = tempUserInfo?.challengeParam?.userAttributes?.['email'];
        
        if (!email) {
          router.push('/auth/login');
          return;
        }
      }
    };

    validateUserWithTempPwdInfo();
  }, [tempUserInfo, userNeedsNewPassword, router]);

  const underAgeModalVisibility = useModalVisibility();

  const [showBirthdateValidation, setShowBirthdateValidation] = useState(false);

  const ageErrorCode = useMemo(
    () => getAgeError(birthDateString),
    [birthDateString]
  );
  const ageErrorMessage = useMemo(() => {
    return errorMessageMapper[ageErrorCode] ?? null;
  }, [ageErrorCode]);

  const onBirthDateChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      setShowBirthdateValidation(true);
      setBirthDateString(event.target.value);
    },
    [setBirthDateString]
  );

  const isContinueButtonDisabled = useMemo(() => {
    if (isUkSelected) {
      return !!ageErrorMessage;
    }
    if (isUsaSelected) {
      return false;
    }
    return true;
  }, [ageErrorMessage, isUkSelected, isUsaSelected]);

  const onContinueButtonClick = useCallback(() => {
    if (ageErrorCode === AGE_ERROR_UNDER_AGE && isUkSelected) {
      underAgeModalVisibility.showModal();
      return;
    }
    setCurrentPage('account_creation');
  }, [setCurrentPage, ageErrorCode, underAgeModalVisibility, isUkSelected]);

  const { data: clientEnvironment } = useClientEnvironment();
  const { 
    name: clientName,
    isEveryset,
  } = clientEnvironment || {};

  return (
    <StyledContainer>
      <ThankyouModal
        bodyContent={
          <p>
            We do not cast anyone 16 years or under in the UK who has not
            completed their last GCSE exam. We would be delighted to invite you
            to register with us once you are of age.
          </p>
        }
        modalVisibility={underAgeModalVisibility}
        header={`Thank you for your interest in joining ${clientName}!`}
      />
      <StyledLogoContainer>
        {isEveryset ? <EverysetLogo width={111} /> : <SaaSLogo />}
      </StyledLogoContainer>
      <StyledGapContainer gap={12}>
        <StyledQuestion>
          <h2>Where will you be working?</h2>
          <p>
            You will be asked to verify your work eligibility before you can
            start working.
          </p>
        </StyledQuestion>
        <StyledGapContainer gap={16}>
          <CountryFlags />
          {isUkSelected && (
            <BirthDatePicker
              birthDate={birthDateString}
              onBirthDateChange={onBirthDateChange}
              ageErrorMessage={ageErrorMessage}
              showBirthdateValidation={showBirthdateValidation}
            />
          )}
        </StyledGapContainer>
      </StyledGapContainer>

      <StyledContinueButton
        onClick={onContinueButtonClick}
        disabled={isContinueButtonDisabled}
        type='primary'
      >
        Continue
      </StyledContinueButton>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  padding: 64px 20px;
  max-width: 448px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: #000000;
`;

const StyledLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
`;

const StyledQuestion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h2 {
    margin: 0;
    line-height: 20px;
    font-weight: 600;
    font-size: 16px;
  }

  p {
    margin: 0;
    font-size: 12px;
    line-height: 16px;
  }
`;

const StyledContinueButton = styled(Button)`
  &.ant-btn {
    height: 32px;

    > span {
      font-size: 12px;
    }

    &:disabled {
      &:hover {
        background: ${({ theme }) => theme.palette.primary[0]};
        border-color: ${({ theme }) => theme.palette.primary[0]};
      }
    }
  }
`;

const AGE_ERROR_NO_DATE = 'AGE_ERROR_NO_DATE';
const AGE_ERROR_INVALID_DATE = 'AGE_ERROR_INVALID_DATE';
const AGE_ERROR_FUTURE_DATE = 'AGE_ERROR_FUTURE_DATE';
const AGE_ERROR_UNDER_AGE = 'AGE_ERROR_UNDER_AGE';

const errorMessageMapper = {
  AGE_ERROR_NO_DATE: 'Please enter your date of birth',
  AGE_ERROR_INVALID_DATE: 'Please enter a valid date',
  AGE_ERROR_FUTURE_DATE: 'Date of Birth can not be in the future',
};

const getAgeError = (birthDate: string | undefined) => {
  if (!birthDate) {
    return AGE_ERROR_NO_DATE;
  }
  const parsedDate = parse(birthDate, 'dd/MM/yyyy', new Date());
  const isValidDate =
    isValid(parsedDate) && format(parsedDate, 'dd/MM/yyyy') === birthDate;

  if (!isValidDate) {
    return AGE_ERROR_INVALID_DATE;
  }

  if (isAfter(parsedDate, new Date())) {
    return AGE_ERROR_FUTURE_DATE;
  }

  const age = differenceInYears(new Date(), parsedDate);
  if (age < 16) {
    return AGE_ERROR_UNDER_AGE;
  }

  return null;
};
